import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import { Table, Thead, Tbody, Tr, Th, Td } from '../../../components/Table/Fake';

import { colors, globalColumnMaxWidth, palette } from '../../../styles/constants';
import { ReportMetadata } from '../../../types/reports';
import Icon from '../../../components/Icons';
import Filters from '../../components/Filters';
import { MonthlyReportGroups } from './ReportList';
import { parseISOStrict } from '../../../helpers/date';
import subDays from 'date-fns/subDays';
import { capitalize } from '../../../helpers/string';
import groupBy from 'lodash/groupBy';
import { getCurrentLanguage } from '../../../i18n';

const attachmentIconMap = new Map<string, 'xml' | 'pdf' | 'tabular' | 'excel'>([
    ['xml', 'xml'],
    ['pdf', 'pdf'],
    ['xsl', 'tabular'],
    ['xlsx', 'excel'],
    ['xls', 'excel'],
    ['csv', 'tabular'],
]);

interface RowProps {
    report: MultiContentReportData;
}

const Row = (
    {
        report,
    }: RowProps
) => {
    const attachements = report.files.map(file => {
        const extension = (file.report_file_name || '').split('.').reverse()[0];
        const iconType = attachmentIconMap.get(extension) || 'tabular';

        return (
            <AttachmentLink key={`report-${file.id}`} href={file.signed_url} download={file.report_file_name} target="_blank" rel="noreferrer">
                <AttachmentIcon pointer={true} icon={iconType} title={file.report_file_name} />
            </AttachmentLink>
        );
    });
    return (
        <Tr key={report.id} className="stop-borders-before-edges">
            <Td className="edge" />
            <PushTd />
            <Td>{report.custom_report_name}</Td>
            <Td>
                <Filters filters={report.report_filters} />
            </Td>
            <Td>
                {attachements}
            </Td>
            <Td className="edge" />
        </Tr>
    );
};

const getMonthString = (yearMonth: string, locale: string) => {
    try {
        const groupDate = parseISOStrict(`${yearMonth}-02`); // using day of month 2 here to avoid any timezone trouble
        if (!groupDate) {
            throw new Error('Invalid group date');
        }
        const yearMonthString = new Intl.DateTimeFormat(locale, {
            year: 'numeric',
            month: 'long',
        }).format(groupDate);
        return yearMonthString;
    } catch {
        return yearMonth;
    }
};

const getDateTimeFormatOptions = (sameYear: boolean, sameMonth: boolean): Intl.DateTimeFormatOptions => {
    if (sameYear && sameMonth) {
        return {
            day: 'numeric',
        };
    }
    if (sameYear) {
        return {
            day: 'numeric',
            month: 'long',
        };
    }
    return {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    };
};

const getGroupDateSpanLabel = (report: ReportMetadata, locale: string) => {
    // Display both the from and to date as inclusive dates so that a report for a given month
    // does not contain dates outside of that month in the time-span label.
    const dataFrom = report?.data_from;
    const dataTo = report?.data_to;
    try {
        if (dataFrom && dataTo) {
            const fromDate = parseISOStrict(dataFrom);
            const toExclusiveDate = parseISOStrict(dataTo);
            if (!fromDate || !toExclusiveDate) {
                throw new Error('Invalid dates');
            }
            const toInclusiveDate = subDays(toExclusiveDate, 1);
            const sameYear = fromDate.getFullYear() === toInclusiveDate.getFullYear();
            const sameMonth = sameYear && fromDate.getMonth() === toInclusiveDate.getMonth();
            const dateTimeFormatOptions = getDateTimeFormatOptions(sameYear, sameMonth);
            const from = new Intl.DateTimeFormat(locale, dateTimeFormatOptions).format(fromDate);
            const to = new Intl.DateTimeFormat(locale, dateTimeFormatOptions).format(toInclusiveDate);
            return `${from} - ${to}`;
        }
    } catch {
        return `${dataFrom} - ${dataTo}`;
    }
};

const getMainReportFromGroup = (reports: MultiContentReportData[]) => {
    const reportWithoutFilter = reports.find((report) => !report.report_filters || report.report_filters.length === 0);
    return reportWithoutFilter || reports.find((report) => report);
};

type MultiContentReportData = Omit<ReportMetadata, 'content_type' | 'report_file_name' | 'signed_url'> & {
    files: {
        content_type?: string;
        report_file_name?: string;
        signed_url?: string;
        id?: string;
    }[];
};

const convertToMultiContentTypeReport = (reports: ReportMetadata[]) => {
    // Old reports does not have report_job_id, and thus needs special handling
    const oldReports: MultiContentReportData[] = reports.filter(report => !report.report_job_id).map(report => ({
        ...report,
        report_job_id: report.id, // Used as key for the report row
        files: [
            {
                content_type: report.content_type,
                report_file_name: report.report_file_name,
                signed_url: report.signed_url,
                id: report.id,
            },
        ],
    }));
    const reportsWithReportId = reports.filter(report => report.report_job_id !== undefined);
    const reportsGroupedByJobId = groupBy(reportsWithReportId, 'report_job_id');
    return [
        ...oldReports,
        ...Object.values(reportsGroupedByJobId).map((reports: ReportMetadata[]) => ({
            ...reports[0], // Reports sharing report_job_id are assumed to be identical, except for the attributes below
            files: reports.map(report => ({
                content_type: report.content_type,
                report_file_name: report.report_file_name,
                signed_url: report.signed_url,
                id: report.id,
            })),
        })),
    ];
};

export interface ReportGroupProps {
    reports: ReportMetadata[];
}

const ReportGroups = (
    {
        reports,
    }: ReportGroupProps
) => {
    const multiContentReports = convertToMultiContentTypeReport(reports);
    const { t, i18n } = useTranslation();
    const locale = getCurrentLanguage(i18n);
    const mainReport = getMainReportFromGroup(multiContentReports);
    if (!mainReport) {
        return null;
    }
    const rest = multiContentReports.filter((report) => report.id !== mainReport.id);
    return (
        <>
            <GroupTitle>
                {getGroupDateSpanLabel(mainReport, locale)} -{' '}
                {t(`reports.report_list.fields.sources.source`, { context: mainReport.template_id })} -{' '}
                {t(`reports.report_list.fields.schedules.schedule`, { context: mainReport.schedule })}
            </GroupTitle>
            <Tbody>
                <Row report={mainReport} />
                {rest.map((report) => (
                    <Row report={report} key={`report-${report.report_job_id}`} />
                ))}
            </Tbody>
        </>
    );
};

export interface ReportsTableProps {
    accountId: string;
    monthlyGroups: MonthlyReportGroups[];
}

const ReportsTable = (
    {
        monthlyGroups,
        accountId,
    }: ReportsTableProps
) => {
    const { t, i18n } = useTranslation();
    const locale = getCurrentLanguage(i18n);
    return (
        <MarginBottomTable className="no-wrap">
            <Thead>
                <Tr>
                    <Th className="edge" />
                    <PushTh />
                    <Th>{t('reports.report_list.fields.name')}</Th>
                    <Th>{t('reports.report_list.fields.filters')}</Th>
                    <AttachmentTh>{t('reports.report_list.fields.file')}</AttachmentTh>
                    <Th className="edge" />
                </Tr>
            </Thead>

            {monthlyGroups.map(({ month, groups }) => (
                <React.Fragment key={month}>
                    <MonthTitle>
                        <Td>
                            {capitalize(getMonthString(month, locale))}
                        </Td>
                        <Td />
                        <Td />
                        <Td />
                        <Td />
                        <Td />
                    </MonthTitle>
                    {groups.map((reports, i) => (
                        <ReportGroups reports={reports} key={`${month}-${i}`} />
                    ))}
                </React.Fragment>
            ))}
        </MarginBottomTable>
    );
};

const MonthTitle = styled(Tr)`
    width: 100%;
    white-space: nowrap;
    & > span {
        background-color: ${palette.neutral[100]};
        color: ${palette.neutral[900]};
        font-size: 16px;
        font-weight: 600;
        padding-inline: var(--Spacing-1);
        padding-block: 8px;
    }
    position: sticky;
    top: 45px;
    z-index: 1;
`;

const GroupTitle = styled.div`
    width: 100%;
    white-space: nowrap;
    /* color: ${colors.primary}; */
    font-size: 14px;
    font-weight: 700;
    margin-left: 32px;
    margin-top: 16px;
`;

const MarginBottomTable = styled(Table)`
    margin-bottom: 64px;
`;

const AttachmentIcon = styled(Icon)`
    margin-left: 2px;
    font-size: 1.3em;
`;
const PushTd = styled(Td)`
    max-width: 16px;
    width: 16px;
`;
const PushTh = styled(Th)`
    max-width: 16px;
    width: 16px;
`;
const AttachmentLink = styled('a')`
    max-width: 16px;
    width: 16px;
    display: inline-block;
    margin-right: 4px;
`;
const AttachmentTh = styled(Th)`
    @media (max-width: ${globalColumnMaxWidth}px) {
        width: 8ch;
    }
`;

export default ReportsTable;
